import { useMemo, useState, useEffect, useCallback } from "react";
import { Box, Snackbar, Tabs, Tab } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import dayjs from "dayjs";
import ClassesFilters from "./components/ClassesFilters";
import ClassesTable from "./components/ClassesTable";
import ClassesActionsPanel from "./components/ClassesActionsPanel";
import ManageClassesView from "./components/ManageClassesView";
import useClassesData from "./hooks/useClassesData";
import useClassesFilters from "./hooks/useClassesFilters";
import { fetchActiveCampuses, fetchAcademicWeeks, fetchClassLists } from "./api/classesApi";

function formatWeekLabel(week) {
  const start = dayjs(week.startDate).format("DD/MM/YY");
  const end = dayjs(week.endDate).format("DD/MM/YY");
  return `${week.weekNumber}: ${start}-${end}`;
}

function ClassesModule({
  selectedYear,
  onSelectStudent,
  selectedDate: externalDate,
  selectedCampusId: externalCampus,
  setSelectedDate: setExternalDate,
  setSelectedCampusId: setExternalCampus,
  showToast: showToastProp,
  onOpenStaffSchedule,
  initialTab,
}) {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [activeTab, setActiveTab] = useState(initialTab || "assign");

  const showToast = useCallback(
    (msg, severity = "success") => {
      if (showToastProp) {
        showToastProp(msg, severity);
      } else {
        setToastMessage(msg);
        setToastSeverity(severity);
        setToastOpen(true);
      }
    },
    [showToastProp]
  );

  const [weeks, setWeeks] = useState([]);
  const [weeksLoading, setWeeksLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);

  // The allocation period the Assign Students tab loads, edits and saves —
  // the ONLY place the module turns its selector into dates. Today the
  // selector is an academic calendar week (whose span is per-campus data:
  // Mon–Fri at some campuses, Mon–Sun at others). Daily class allocation is a
  // different selector producing a one-day period; the hook, the API and the
  // backend take { startDate, endDate } and never assume a week.
  const allocationPeriod = useMemo(
    () =>
      selectedWeek
        ? { startDate: selectedWeek.startDate, endDate: selectedWeek.endDate }
        : null,
    [selectedWeek]
  );
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const [campuses, setCampuses] = useState([]);
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedEnglishLevel, setSelectedEnglishLevel] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadCampuses() {
      try {
        const data = await fetchActiveCampuses();
        if (!mounted) return;
        setCampuses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch active campuses", err);
        if (!mounted) return;
        setCampuses([]);
      }
    }

    loadCampuses();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedCampusId || !selectedYear) {
      setWeeks([]);
      setSelectedWeek(null);
      return;
    }

    let mounted = true;
    setWeeksLoading(true);

    fetchAcademicWeeks(selectedCampusId, selectedYear)
      .then((data) => {
        if (!mounted) return;
        const sorted = (Array.isArray(data) ? data : []).slice().sort(
          (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
        );
        setWeeks(sorted);
      })
      .catch((err) => {
        console.error("Failed to fetch academic weeks", err);
        if (!mounted) return;
        setWeeks([]);
      })
      .finally(() => {
        if (mounted) setWeeksLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [selectedCampusId, selectedYear]);

  useEffect(() => {
    if (!weeks.length) {
      setSelectedWeek(null);
      return;
    }

    if (selectedWeek && weeks.some((w) => w.id === selectedWeek.id)) return;

    if (!externalDate) {
      setSelectedWeek(weeks[0]);
      return;
    }

    const d = dayjs(externalDate);
    if (!d.isValid()) {
      setSelectedWeek(weeks[0]);
      return;
    }

    const matchingWeek =
      weeks.find((week) => {
        const start = dayjs(week.startDate);
        const end = dayjs(week.endDate);

        return (
          d.isSame(start, "day") ||
          d.isSame(end, "day") ||
          (d.isAfter(start, "day") && d.isBefore(end, "day"))
        );
      }) || weeks[0];

    setSelectedWeek(matchingWeek);
  }, [externalDate, weeks, selectedWeek]);

  useEffect(() => {
    setSelectedCampusId(externalCampus ? String(externalCampus) : "");
  }, [externalCampus]);

  useEffect(() => {
    setSelectedAge("");
    setSelectedEnglishLevel("");
    setSelectedClass("");
    setNameSearch("");
    setSortField("");
    setSortAsc(true);
  }, [selectedYear]);

  const campusOptions = useMemo(() => {
    return campuses
      .map((campus) => ({
        value: String(campus.id ?? ""),
        label: String(campus.campusName ?? campus.name ?? ""),
      }))
      .filter((c) => c.value && c.label)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [campuses]);

  const selectedCampusName = useMemo(() => {
    if (!selectedCampusId) return "";

    const match = campusOptions.find(
      (campus) => campus.value === String(selectedCampusId)
    );

    return match?.label || "";
  }, [selectedCampusId, campusOptions]);

  const {
    classlists,
    classes,
    changes,
    loading,
    handleClassChange,
    handleSaveChanges,
    refreshData,
  } = useClassesData({
    period: allocationPeriod,
    selectedCampus: selectedCampusName,
    showToast,
  });

  const {
    filteredClasslists,
    sortedClasslists,
    ageOptions,
    levelOptions,
    classOptions,
  } = useClassesFilters({
    classlists,
    selectedCampus: selectedCampusName,
    selectedAge,
    selectedEnglishLevel,
    selectedClass,
    nameSearch,
    sortField,
    sortAsc,
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const handleWeekChange = (week) => {
    setSelectedWeek(week);

    if (week?.startDate) {
      setExternalDate?.(dayjs(week.startDate));
    }
  };

  const handleCampusChange = (value) => {
    const nextCampusId = value || "";
    setSelectedCampusId(nextCampusId);
    setExternalCampus?.(nextCampusId);
  };

  const weekLabel = selectedWeek ? formatWeekLabel(selectedWeek) : "";

  const [copying, setCopying] = useState(false);

  const handleCopyFromPreviousWeek = useCallback(async () => {
    if (!selectedWeek || !selectedCampusName) return;

    const previousWeek = weeks
      .filter((w) =>
        dayjs(w.startDate).isBefore(dayjs(selectedWeek.startDate))
      )
      .sort(
        (a, b) =>
          dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf()
      )[0];

    if (!previousWeek) {
      showToast("No earlier week to copy from.", "info");
      return;
    }

    setCopying(true);
    try {
      const previousRows = await fetchClassLists(previousWeek, selectedCampusName);

      if (!Array.isArray(previousRows) || previousRows.length === 0) {
        showToast("No assignments found in the previous week.", "info");
        return;
      }

      const previousByStudent = new Map();
      previousRows.forEach((row) => {
        if (row.studentId && row.group_name) {
          previousByStudent.set(row.studentId, row.group_name);
        }
      });

      const currentClassNames = new Set(
        classes.map((c) =>
          String(c.groupName || "").trim().toLowerCase()
        )
      );

      let copied = 0;
      let skippedNoMatch = 0;
      let skippedAlreadyAssigned = 0;
      let skippedNotInPrevious = 0;

      classlists.forEach((student) => {
        if (student.group_name) {
          skippedAlreadyAssigned++;
          return;
        }
        const previousGroupName = previousByStudent.get(student.studentId);
        if (!previousGroupName) {
          skippedNotInPrevious++;
          return;
        }
        if (
          !currentClassNames.has(
            String(previousGroupName).trim().toLowerCase()
          )
        ) {
          skippedNoMatch++;
          return;
        }
        handleClassChange(student.studentId, "group_name", previousGroupName);
        copied++;
      });

      const parts = [];
      parts.push(`${copied} copied`);
      if (skippedAlreadyAssigned) parts.push(`${skippedAlreadyAssigned} already assigned`);
      if (skippedNoMatch) parts.push(`${skippedNoMatch} had no matching class this week`);
      if (skippedNotInPrevious) parts.push(`${skippedNotInPrevious} new this week`);

      showToast(
        copied > 0
          ? `${parts.join(", ")}. Click Save Changes to apply.`
          : parts.join(", "),
        copied > 0 ? "success" : "info"
      );
    } catch (err) {
      console.error("Failed to copy class allocations from previous week", err);
      showToast("Failed to copy allocations from previous week.", "error");
    } finally {
      setCopying(false);
    }
  }, [
    selectedWeek,
    selectedCampusName,
    weeks,
    classlists,
    classes,
    handleClassChange,
    showToast,
  ]);

  return (
    <Box sx={{ width: "100%", typography: "body1", p: 2 }}>
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab value="assign" label="Assign students" />
        <Tab value="manage" label="Manage classes" />
      </Tabs>

      <ClassesFilters
        weeks={weeks}
        weeksLoading={weeksLoading}
        formatWeekLabel={formatWeekLabel}
        selectedWeek={selectedWeek}
        setSelectedWeek={handleWeekChange}
        selectedCampus={selectedCampusId}
        setSelectedCampus={handleCampusChange}
        selectedAge={selectedAge}
        setSelectedAge={setSelectedAge}
        selectedEnglishLevel={selectedEnglishLevel}
        setSelectedEnglishLevel={setSelectedEnglishLevel}
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
        nameSearch={nameSearch}
        setNameSearch={setNameSearch}
        campusOptions={campusOptions}
        ageOptions={ageOptions}
        levelOptions={levelOptions}
        classOptions={classOptions}
        showStudentFilters={activeTab === "assign"}
      />

      {activeTab === "assign" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ maxWidth: 1200, width: "100%" }}>
            <ClassesTable
              rows={sortedClasslists}
              classes={classes}
              selectedCampus={selectedCampusName}
              loading={loading}
              onSort={handleSort}
              onSelectStudent={onSelectStudent}
              onClassChange={handleClassChange}
              onSaveChanges={() =>
                handleSaveChanges({
                  visibleRows: filteredClasslists,
                  selectedWeek,
                  refreshData,
                })
              }
            />
          </Box>

          <ClassesActionsPanel
            canSave={!!selectedCampusId && changes.length > 0}
            canExport={filteredClasslists.length > 0}
            rows={sortedClasslists}
            selectedYear={selectedYear}
            selectedCampus={selectedCampusName}
            onSave={() =>
              handleSaveChanges({
                visibleRows: filteredClasslists,
                refreshData,
              })
            }
            onCopyFromPreviousWeek={handleCopyFromPreviousWeek}
            canCopyFromPreviousWeek={
              !!selectedWeek && !!selectedCampusName && classlists.length > 0
            }
            copying={copying}
          />
        </Box>
      )}

      {activeTab === "manage" && (
        <ManageClassesView
          selectedCampus={selectedCampusId}
          selectedCampusName={selectedCampusName}
          selectedWeek={selectedWeek}
          weekLabel={weekLabel}
          allWeeks={weeks}
          formatWeekLabel={formatWeekLabel}
          showToast={showToast}
          onClassesChanged={refreshData}
          onOpenStaffSchedule={onOpenStaffSchedule}
        />
      )}

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          elevation={6}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default ClassesModule;